// 1. React imports
import { useState } from 'react';

// 2. External libraries
import { CircleCheck, RefreshCw } from 'lucide-react';

// 3. Internal components
import { Alert, AlertDescription } from '~/components/ui/alert';
import { Button } from '~/components/ui/button';
import { Spinner } from '~/components/ui/spinner';
import { TemplateCodeBlock } from '~/components/common/TemplateCodeBlock';

// 4. Type imports
import type { AnyWebSocketEvent } from '~/types/websocket';
import { isOnboardingMessageRetrievedEvent } from '~/types/websocket';
import type { UsageType } from '~/utils/exampleDatasets';

// 5. Hooks and utilities
import { useWebSocketSubscription } from '~/hooks/useWebSocketSubscription';
import { useConnectionsQuery } from '~/hooks/useConnectionsQuery';
import { EXAMPLE_DATASETS } from '~/utils/exampleDatasets';
import { getApiBaseUrl } from '~/lib/api';

/**
 * MCP templates for message retrieval configuration.
 * Per §BASIC.2: Reuse centralized templates from exampleDatasets.ts
 * to maintain consistency with McpConnectionContent.tsx.
 */
const MCP_TEMPLATES = EXAMPLE_DATASETS.mcp_examples.usageTypes;

/**
 * Generate GET request retrieval templates based on template type.
 * Converts POST message templates to GET retrieval requests.
 * Per §BASIC.2: Extract template generation to avoid duplication.
 */
function generateRetrievalCode(
  templateId: string,
  apiKey: string,
  baseUrl: string,
  topicId: string
): string {
  switch (templateId) {
    case 'curl':
      return `curl -H "X-API-Key: ${apiKey}" \\\n  ${baseUrl}/v1/topics/${topicId}/messages`;
    case 'curl-query':
      return `curl "${baseUrl}/v1/topics/${topicId}/messages?api_key=${apiKey}"`;
    case 'axios':
      return (
        `import axios from 'axios';\n\n` +
        `const response = await axios.get(\n` +
        `  '${baseUrl}/v1/topics/${topicId}/messages',\n` +
        `  {\n` +
        `    headers: {\n` +
        `      'X-API-Key': '${apiKey}'\n` +
        `    }\n` +
        `  }\n` +
        `);`
      );
    case 'fetch':
      return (
        `const response = await fetch('${baseUrl}/v1/topics/${topicId}/messages', {\n` +
        `  headers: {\n` +
        `    'X-API-Key': '${apiKey}'\n` +
        `  }\n` +
        `});\n\n` +
        `const messages = await response.json();`
      );
    case 'python-requests':
      return (
        `import requests\n\n` +
        `response = requests.get(\n` +
        `    '${baseUrl}/v1/topics/${topicId}/messages',\n` +
        `    headers={\n` +
        `        'X-API-Key': '${apiKey}'\n` +
        `    }\n` +
        `)\n\n` +
        `messages = response.json()`
      );
    case 'powershell':
      return (
        `$headers = @{\n` +
        `    "X-API-Key" = "${apiKey}"\n` +
        `}\n\n` +
        `Invoke-RestMethod -Uri "${baseUrl}/v1/topics/${topicId}/messages" \`\n` +
        `    -Method Get \`\n` +
        `    -Headers $headers`
      );
    default:
      return '';
  }
}

/**
 * REST templates for message retrieval.
 * Per §BASIC.2: Reuse base templates from exampleDatasets.ts but adapt for GET requests.
 */
const REST_TEMPLATES = EXAMPLE_DATASETS.rest_examples.usageTypes;

/**
 * Props for RetrieveMessageStep component.
 *
 * Per §TS.3.2: Descriptive props interface naming.
 */
export interface RetrieveMessageStepProps {
  topicId: string;
  messageId: string | null;
  messageContent: string | null;
  clientType: 'mcp' | 'rest';
  apiKey: string;
  onComplete: () => void;
  onBack: () => void;
  onSkip: () => void;
  showMessageSuccess?: boolean;
}

/**
 * Step for retrieving message during onboarding wizard.
 *
 * Shows message preview and displays two-step retrieval instructions based on clientType:
 * - MCP clients: Step 1 = MCP configuration, Step 2 = suggested prompts for LLM
 * - REST clients: Step 1 = REST endpoint code, Step 2 = expected response format
 *
 * Waits for onboarding.message_retrieved WebSocket event, then calls onComplete
 * to advance to step 7 (CompletionStep). A "Check again" button re-queries the
 * read-capable connection as a manual fallback for a dropped WebSocket connection.
 *
 * Per §TS.3.1: Function declaration for named export.
 * Per §TS.2.3: WebSocket event type guards for safe event handling.
 *
 * @example
 * ```tsx
 * <RetrieveMessageStep
 *   topicId="topic_123"
 *   messageId="msg_abc"
 *   messageContent="Hello from DeadDrop!"
 *   clientType="rest"
 *   apiKey="ddrp_rest_abc123"
 *   onComplete={() => navigate('/topics')}
 *   onBack={() => setStep(3)}
 *   onSkip={handleSkip}
 * />
 * ```
 */
export function RetrieveMessageStep({
  topicId,
  messageId: _messageId,
  messageContent: _messageContent,
  clientType,
  apiKey,
  onComplete,
  onBack,
  onSkip,
  showMessageSuccess = false,
}: RetrieveMessageStepProps) {
  // Listen for WebSocket events using subscription (no new connection)
  useWebSocketSubscription((event: AnyWebSocketEvent) => {
    // Filter events by type and topicId match (per spec)
    if (isOnboardingMessageRetrievedEvent(event) && event.data.topicId === topicId) {
      // Call onComplete to advance to step 7 (CompletionStep)
      onComplete();
    }
  });

  // Manual fallback for when the WebSocket connection drops or the retrieval
  // event was missed: re-query the read-capable connection and advance if
  // it has already caught up on this topic's messages.
  const [showNotRetrievedYet, setShowNotRetrievedYet] = useState<boolean>(false);
  const connectionsQuery = useConnectionsQuery(topicId, { type: clientType, enabled: false });

  const handleCheckAgain = async () => {
    setShowNotRetrievedYet(false);
    // Fetches connections filtered by type, then reads the first match. There is no
    // dedicated single-connection-by-topic-and-type endpoint; this mirrors the existing
    // precedent for `messagesBehind` in ConnectionsTab.tsx.
    const result = await connectionsQuery.refetch();
    const connection = result.data?.[0];
    if (connection && connection.messagesBehind === 0) {
      onComplete();
      return;
    }
    setShowNotRetrievedYet(true);
  };

  // Generate configuration code for templates
  // Per §BASIC.2: Reuse templates from exampleDatasets.ts for consistency
  const generateCode = (template: UsageType): string => {
    const baseUrl = getApiBaseUrl();

    // MCP templates use original config generation
    if (clientType === 'mcp') {
      const { code } = template;
      return typeof code === 'function' ? code({ apiKey, baseUrl }) : code;
    }

    // REST templates use custom retrieval code for GET requests
    return generateRetrievalCode(template.id, apiKey, baseUrl, topicId);
  };

  return (
    <div>
      {/* Success Alert - shown when message was just received */}
      {showMessageSuccess && (
        <Alert variant="success" className="mb-6">
          <CircleCheck />
          <AlertDescription>
            Message received! Your message was successfully delivered.
          </AlertDescription>
        </Alert>
      )}

      <p className="mb-6 text-sm text-muted-foreground">
        Use your {clientType === 'mcp' ? 'MCP client' : 'REST API'} to retrieve the message you just
        sent.
      </p>

      {/* Step 1: Setup/Configuration */}
      <p className="mb-4 font-heading text-base font-semibold">
        Step 1: {clientType === 'mcp' ? 'Add MCP Configuration' : 'Retrieve Messages'}
      </p>
      <TemplateCodeBlock
        templates={clientType === 'mcp' ? MCP_TEMPLATES : REST_TEMPLATES}
        generateCode={generateCode}
        legend={clientType === 'mcp' ? 'Configuration to add to your MCP client' : 'API Request'}
        minRows={clientType === 'mcp' ? 8 : 5}
      />

      {/* Step 2: Usage/Prompts */}
      <p className="mt-6 mb-4 font-heading text-base font-semibold">
        Step 2: {clientType === 'mcp' ? 'Ask Your LLM' : 'Review Response'}
      </p>
      {clientType === 'mcp' ? (
        <p className="mb-6 text-sm text-muted-foreground">
          Try these prompts with your LLM:
          <br />
          <br />
          1. &quot;Read messages from my DeadDrop topic&quot;
          <br />
          2. &quot;Check for new messages in topic {topicId}&quot;
          <br />
          3. &quot;Show me the latest message from DeadDrop&quot;
        </p>
      ) : (
        <p className="mb-6 text-sm text-muted-foreground">
          When you execute the request, you&apos;ll receive a JSON response containing your
          messages. Each message includes an ID, content, and timestamp.
        </p>
      )}

      {/* Waiting Indicator */}
      <div className="mt-6 mb-6 flex items-center gap-4 rounded border border-border bg-card p-4">
        <Spinner className="size-6" data-testid="retrieve-message-waiting-spinner" />
        <p className="text-sm text-muted-foreground">Waiting for message retrieval...</p>
      </div>

      {/* Manual fallback for a dropped WebSocket connection */}
      <div className="mb-6 flex flex-col gap-3">
        <Button
          variant="outline"
          onClick={() => void handleCheckAgain()}
          disabled={connectionsQuery.isFetching}
        >
          {connectionsQuery.isFetching ? (
            <Spinner data-icon="inline-start" aria-hidden="true" />
          ) : (
            <RefreshCw data-icon="inline-start" />
          )}
          Check again
        </Button>
        {showNotRetrievedYet && (
          <Alert>
            <AlertDescription>
              No retrieval detected yet. Make sure you&apos;ve run the request above, then check
              again.
            </AlertDescription>
          </Alert>
        )}
      </div>

      {/* Back and Skip Buttons */}
      <div className="flex justify-end gap-4">
        <Button variant="ghost" onClick={onSkip}>
          Skip onboarding
        </Button>
        <Button variant="outline" onClick={onBack}>
          Back
        </Button>
      </div>
    </div>
  );
}
